import React, {useState} from "react";
import {ArrowLeft, ArrowRight, BookOpen, Check, LockKeyhole, ShoppingCart, Trash2} from "lucide-react";
import {useNavigate, useSearchParams} from "react-router-dom";
import CourseNavbar from "../components/landing/CourseNavbar";
import {useCart} from "../components/CardContext";
import GoogleOneTap from "../components/GoogleOneTap";
import authSession from "../lib/authSession";
import {useUserAuth, useUserLearning} from "../store/user";

const formatNaira = (amount) => `₦${Number(amount || 0).toLocaleString("en-NG")}`;
const itemTypeForApi = (type) => {
  const normalized = String(type || "course").toUpperCase().replace(/[\s-]+/g, "_").replace(/S$/, "");
  return ({
    COURSE: "COURSE", BOOK: "BOOK", JOURNAL: "JOURNAL", PROJECT_TOPIC: "PROJECT_TOPIC",
    SEMINAR_TOPIC: "SEMINAR_TOPIC", RESEARCH_MATERIAL: "RESEARCH_MATERIAL", MANUAL: "MANUAL",
  })[normalized] || null;
};

const Cart = () => {
  const {cart, removeFromCart, clearCart} = useCart();
  const {userIsAuthenticated, userGoogleLogin} = useUserAuth();
  const {addUserCartItem, checkoutUserCart} = useUserLearning();
  const authSnapshot = React.useSyncExternalStore(authSession.subscribe, authSession.getSnapshot);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const autoCheckoutStarted = React.useRef(false);
  const [loginPrompt, setLoginPrompt] = useState(false);
  const [googleError, setGoogleError] = useState("");
  const [sessionReady, setSessionReady] = useState(authSnapshot.status === "authenticated");
  const authenticated = userIsAuthenticated || (authSnapshot.status === "authenticated" && authSnapshot.role === "user");
  const subtotal = cart.reduce((sum, item) => sum + (Number(item.price) || 0), 0);

  React.useEffect(() => {
    let active = true;
    authSession.ensureSession("user").catch(() => {}).finally(() => {
      if (active) setSessionReady(true);
    });
    return () => { active = false; };
  }, []);

  React.useEffect(() => {
    if (authenticated) {
      setLoginPrompt(false);
      setGoogleError("");
    }
  }, [authenticated]);

  const detailPath = (item) => {
    const type = String(item.type || "course").toLowerCase().replace(/_/g, "-");
    if (type.includes("course")) return `/courses/${item.id}`;
    const pluralType = type.endsWith("s") ? type : `${type}s`;
    return `/materials/${pluralType}/${item.id}`;
  };

  const startCheckout = React.useCallback(async (authenticatedNow = false) => {
    if (!cart.length || checkoutLoading) return;
    let hasSession = authenticated;
    if (!hasSession && !authenticatedNow && !sessionReady) {
      await authSession.ensureSession("user").catch(() => {});
      hasSession = authSession.isAuthenticatedFor("user");
    }
    if (!hasSession && !authenticatedNow) {
      setLoginPrompt(true);
      setGoogleError("");
      return;
    }

    setCheckoutLoading(true);
    setCheckoutError(null);
    const next = new URLSearchParams(searchParams);
    ["checkoutId", "checkoutType", "checkout"].forEach((key) => next.delete(key));
    setSearchParams(next, {replace: true});

    for (const item of cart) {
      const itemType = itemTypeForApi(item.type);
      if (!itemType) {
        setCheckoutError(`“${item.title || "An item"}” cannot be checked out yet.`);
        setCheckoutLoading(false);
        return;
      }
      const staged = await addUserCartItem(itemType, item.id);
      if (!staged.success) {
        setCheckoutError(staged.error || `Could not prepare “${item.title || "this item"}” for checkout.`);
        setCheckoutLoading(false);
        return;
      }
    }

    const result = await checkoutUserCart();
    setCheckoutLoading(false);
    if (!result.success) {
      setCheckoutError(result.error || "Could not start checkout. Please try again.");
      return;
    }
    const payload = result.data || {};
    const redirectUrl = payload.authorizationUrl || payload.authorization_url || payload.paymentUrl;
    if (redirectUrl) {
      window.location.href = redirectUrl;
      return;
    }
    if (payload.reference) {
      navigate(`/payment-success?reference=${encodeURIComponent(payload.reference)}`);
      return;
    }
    setCheckoutError("Checkout started but no payment link was returned.");
  }, [addUserCartItem, authenticated, cart, checkoutLoading, checkoutUserCart, navigate, searchParams, sessionReady, setSearchParams]);

  // Buy Now and the email/password fallback both return here with checkout intent.
  React.useEffect(() => {
    const hasIntent = searchParams.has("checkout") || (searchParams.has("checkoutId") && searchParams.has("checkoutType"));
    if (!hasIntent || !cart.length || checkoutLoading || autoCheckoutStarted.current || !sessionReady) return;
    const selectedId = searchParams.get("checkoutId");
    const selectedType = searchParams.get("checkoutType");
    if (selectedId && !cart.some((item) => String(item.id) === selectedId && String(item.type || "course").toLowerCase() === String(selectedType).toLowerCase())) return;
    autoCheckoutStarted.current = true;
    if (!authenticated) setLoginPrompt(true);
    else startCheckout();
  }, [authenticated, cart, checkoutLoading, searchParams, sessionReady, startCheckout]);

  const handleGoogleSuccess = async (response) => {
    setGoogleError("");
    const result = await userGoogleLogin(response.credential);
    if (!result.success) {
      setGoogleError(result.error || "Google sign-in failed. Please try again.");
      return;
    }
    setLoginPrompt(false);
    startCheckout(true);
  };

  const closeLoginPrompt = () => {
    setLoginPrompt(false);
    setGoogleError("");
    if (searchParams.has("checkoutId") || searchParams.has("checkout")) {
      const next = new URLSearchParams(searchParams);
      ["checkoutId", "checkoutType", "checkout"].forEach((key) => next.delete(key));
      setSearchParams(next, {replace: true});
    }
  };

  return (
    <div className="min-h-screen bg-[#f7f7f9] text-slate-900">
      <CourseNavbar />
      {loginPrompt ? (
        <div className="fixed inset-0 z-[100000] flex items-center justify-center bg-slate-950/55 p-4" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) closeLoginPrompt(); }}>
          <section role="dialog" aria-modal="true" aria-labelledby="checkout-login-title" className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl sm:p-8">
            <button type="button" onClick={closeLoginPrompt} aria-label="Close sign-in prompt" className="float-right rounded-md px-2 py-1 text-slate-500 hover:bg-slate-100">×</button>
            <h2 id="checkout-login-title" className="mt-2 text-xl font-bold text-slate-900">Sign in to continue to checkout</h2>
            <p className="mt-2 text-sm text-slate-600">Continue with Google to securely pay for your cart. Your items will be here when you return.</p>
            <div className="mt-5">
              <GoogleOneTap onSuccess={handleGoogleSuccess} onError={(error) => setGoogleError(error?.message || "Google sign-in is unavailable right now.")} enabled={!authenticated} autoPrompt renderButton width={340} />
            </div>
            {googleError ? <p role="alert" className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">{googleError}</p> : null}
            <div className="mt-5 flex items-center gap-3 text-xs text-slate-400"><span className="h-px flex-1 bg-slate-200" />or<span className="h-px flex-1 bg-slate-200" /></div>
            <button type="button" onClick={() => navigate(`/login?next=${encodeURIComponent("/cart?checkout=true")}`)} className="mt-4 w-full rounded-md border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">Use email and password</button>
          </section>
        </div>
      ) : null}

      <main className="mx-auto max-w-6xl px-4 pb-16 pt-8 sm:px-6 lg:px-8">
        <button type="button" onClick={() => navigate("/")} className="mb-7 inline-flex items-center gap-2 text-sm font-semibold text-slate-600 transition hover:text-slate-950">
          <ArrowLeft className="h-4 w-4" /> Continue shopping
        </button>

        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="mt-2 text-3xl font-black tracking-tight">Shopping cart</h1>
            <p className="mt-2 text-slate-600">{cart.length} item{cart.length === 1 ? "" : "s"} in your cart</p>
          </div>
          {cart.length > 0 ? <button type="button" onClick={clearCart} className="rounded-lg px-3 py-2 text-sm font-semibold text-slate-500 transition hover:bg-white hover:text-rose-700">Clear cart</button> : null}
        </div>

        {checkoutError ? <p role="alert" className="mb-5 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-700">{checkoutError}</p> : null}

        {cart.length ? (
          <div className="grid items-start gap-8 lg:grid-cols-[minmax(0,1fr)_340px]">
            <section aria-label="Cart items" className="min-w-0">
              <div className="mb-3 flex items-center justify-between text-sm font-semibold text-slate-500"><span>{cart.length} item{cart.length === 1 ? "" : "s"}</span><span>Price</span></div>
              <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                {cart.map((item, index) => (
                  <article key={`${item.type || "course"}-${item.id}`} className={`flex gap-4 p-4 sm:gap-5 sm:p-5 ${index ? "border-t border-slate-100" : ""}`}>
                    <button type="button" onClick={() => navigate(detailPath(item))} className="h-24 w-32 shrink-0 overflow-hidden rounded-lg bg-slate-100 text-left sm:h-28 sm:w-40">
                      <img src={item.thumbnail || "/images/course_placeholder.jpg"} alt="" className="h-full w-full object-cover transition duration-300 hover:scale-105" loading="lazy" />
                    </button>
                    <div className="flex min-w-0 flex-1 flex-col sm:flex-row sm:justify-between sm:gap-5">
                      <div className="min-w-0">
                        <button type="button" onClick={() => navigate(detailPath(item))} className="line-clamp-2 text-left font-bold leading-snug text-slate-900 hover:text-blue-900">{item.title || "Learning resource"}</button>
                        <p className="mt-1 text-sm text-slate-500">{item.instructor || item.author || item.type || "Learning resource"}</p>
                        <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500">
                          {item.category ? <span>{item.category}</span> : null}{item.duration ? <span>{item.duration}</span> : null}{item.level ? <span>{item.level}</span> : null}
                        </div>
                        <div className="mt-3 flex gap-4">
                          <button type="button" onClick={() => removeFromCart(item.id, item.type)} className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-rose-700"><Trash2 className="h-3.5 w-3.5" /> Remove</button>
                          <button type="button" onClick={() => navigate(detailPath(item))} className="text-xs font-semibold text-blue-900 hover:text-blue-800">View details</button>
                        </div>
                      </div>
                      <p className="mt-3 shrink-0 text-lg font-extrabold text-slate-900 sm:mt-0">{formatNaira(item.price)}</p>
                    </div>
                  </article>
                ))}
              </div>
              {cart.length > 3 ? <p className="mt-3 text-sm text-slate-500">Your total includes all {cart.length} items in the cart.</p> : null}
            </section>

            <aside className="lg:sticky lg:top-6">
              <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
                <h2 className="text-lg font-bold">Order summary</h2>
                <div className="mt-5 flex justify-between text-sm text-slate-600"><span>Subtotal ({cart.length} items)</span><span>{formatNaira(subtotal)}</span></div>
                <div className="my-5 border-t border-slate-200" />
                <div className="flex items-end justify-between gap-3"><span className="font-bold">Total</span><span className="text-2xl font-black tracking-tight">{formatNaira(subtotal)}</span></div>
                <button type="button" onClick={() => startCheckout()} disabled={checkoutLoading || !cart.length} className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-sidebar px-5 py-3.5 font-bold text-sidebar-foreground shadow-sm transition hover:bg-blue-900 disabled:cursor-wait disabled:opacity-60">
                  {checkoutLoading ? "Preparing checkout…" : "Proceed to checkout"}<ArrowRight className="h-4 w-4" />
                </button>
                <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-slate-500"><LockKeyhole className="h-3.5 w-3.5" /> Secure checkout · Paystack</p>
                <div className="mt-6 rounded-lg bg-blue-50 p-4 text-sm text-blue-950"><p className="flex items-center gap-2 font-bold"><Check className="h-4 w-4" /> Keep learning</p><p className="mt-1 text-blue-900/75">Your purchased resources will be available in My Library.</p></div>
              </div>
            </aside>
          </div>
        ) : (
          <section className="rounded-xl border border-slate-200 bg-white px-5 py-16 text-center shadow-sm">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-xl bg-blue-50 text-blue-900"><ShoppingCart className="h-8 w-8" /></div>
            <h2 className="mt-5 text-xl font-bold">Your cart is empty</h2>
            <p className="mt-2 text-sm text-slate-500">Find a course, book or resource to add to your learning plan.</p>
            <button type="button" onClick={() => navigate("/")} className="mt-6 inline-flex items-center gap-2 rounded-lg bg-sidebar px-5 py-3 font-bold text-sidebar-foreground hover:bg-blue-900"><BookOpen className="h-4 w-4" /> Explore courses</button>
          </section>
        )}
      </main>
    </div>
  );
};

export default Cart;
